import { css } from "../../../styled-system/css";
import { hstack, vstack } from "../../../styled-system/patterns";
import { Color, Variant } from "../../api/schemas";
import svgCheckboxChecked from "../../assets/icons/checkboxChecked.svg";
import svgCheckboxUnchecked from "../../assets/icons/checkboxUnchecked.svg";
import { PageTitle } from "../../components/PageTitle";
import { SquareButton } from "../../components/buttons/squareButton/SquareButton";
import { Chips } from "../../components/chips/Chips";
import { ConsentedCheckboxes } from "../../types/form";
import { Path } from "../../types/route";

const valueContainer = css({
  display: "flex",
  backgroundColor: "primary.contrast",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "border.secondary",
  flexDirection: "row",
  width: "100%",
  textStyle: "span.m.bold",
  justifyContent: "space-between",
  alignContent: "center",
  pl: 3,
  pr: 1,
  py: 1.5,
});

const valueText = css({
  display: "flex",
  alignItems: "center",
  textStyle: "button.s",
});

export interface ConfirmProps {
  setPath: (path: Path) => void;
  variantData:
    | {
        left: Variant;
        right: Variant;
        heel: string;
        color: Color;
        totalPrice: number;
      }
    | undefined;
  parentCode: string | undefined;
  isConsented: ConsentedCheckboxes;
  onClickCheckbox: (isConsented: ConsentedCheckboxes) => void;
  onClickAddingToCart: () => void;
}

function isAdditionalConsented(parentCode: string | undefined): boolean {
  const additionalConsentedRequiredCodes = ["CD0127739301", "CD0127739401"];
  return parentCode
    ? additionalConsentedRequiredCodes.includes(parentCode)
    : false;
}

export function Confirm({
  setPath,
  variantData,
  isConsented,
  parentCode,
  onClickCheckbox,
  onClickAddingToCart,
}: ConfirmProps) {
  return (
    <div className={vstack({ width: "100%", gap: 0 })}>
      <div className={css({ width: "100%", maxWidth: "760px", px: 3 })}>
        <div className={css({ mb: 3 })}>
          <PageTitle>最終確認</PageTitle>
        </div>

        <img
          className={css({
            display: "block",
            mb: 3,
            mx: "auto",
            textAlign: "center",
            maxWidth: "180px",
          })}
          src={variantData?.color.image_url}
          alt=""
        />

        <div
          className={css({
            position: "relative",
            width: "100%",
            mb: 4,
            textAlign: "center",
          })}
        >
          <span
            className={css({
              textStyle: "span.m.bold",
              position: "relative",
              display: "inline-block",
              bg: "black.100",
              zIndex: 2,
              pl: 1,
              pr: 1,
            })}
          >
            注文内容をご確認ください
          </span>
          <hr
            className={css({
              position: "absolute",
              top: "50%",
              width: "100%",
              zIndex: 1,
            })}
          />
        </div>

        <div className={vstack({ gap: "24px 0", width: "100%" })}>
          <div className={hstack({ gap: 0, width: "100%" })}>
            <span
              className={css({ textStyle: "span.m.bold", minWidth: "99px" })}
            >
              ヒール
            </span>
            <div className={valueContainer}>
              <span className={valueText}>{variantData?.heel}</span>
            </div>
          </div>

          <div className={hstack({ gap: 0, width: "100%" })}>
            <span
              className={css({ textStyle: "span.m.bold", minWidth: "99px" })}
            >
              カラー
            </span>
            <div
              className={hstack({
                gap: 0,
                justify: "space-between",
                alignItems: "center",
                width: "100%",
              })}
            >
              <div className={hstack({ gap: "0 8px" })}>
                <div
                  style={{
                    backgroundColor: variantData?.color.color_code
                      ? `#${variantData.color.color_code}`
                      : "",
                    width: "32px",
                    height: "32px",
                    borderRadius: "4px",
                  }}
                />
                <span className={css({ textStyle: "button.s" })}>
                  {variantData?.color.value}
                </span>
              </div>
              <Chips
                tag="button"
                text="変更"
                variant="borderSecondaryOutlined"
                size="s"
                onClick={() => {
                  setPath("change_color");
                }}
                fontWeight="bold"
              />
            </div>
          </div>

          <div className={css({ textAlign: "left", width: "100%;" })}>
            <Chips
              text="右足"
              variant="backgroundDarkenContained"
              size="l"
              fontWeight="bold"
            />
          </div>
          <div className={hstack({ gap: 0, width: "100%" })}>
            <span
              className={css({ textStyle: "span.m.bold", minWidth: "99px" })}
            >
              幅(ワイズ)
            </span>
            <div className={valueContainer}>
              <span className={valueText}>
                {variantData?.right.foot_width.value}
              </span>
              <Chips
                tag="button"
                text="変更"
                variant="borderSecondaryOutlined"
                size="s"
                onClick={() => {
                  setPath("change_width_and_size");
                }}
                fontWeight="bold"
              />
            </div>
          </div>

          <div className={hstack({ gap: 0, width: "100%" })}>
            <span
              className={css({ textStyle: "span.m.bold", minWidth: "99px" })}
            >
              足長(サイズ)
            </span>
            <div className={valueContainer}>
              <span className={valueText}>
                {variantData?.right.foot_length.value}
              </span>
              <Chips
                tag="button"
                text="変更"
                variant="borderSecondaryOutlined"
                size="s"
                onClick={() => {
                  setPath("change_width_and_size");
                }}
                fontWeight="bold"
              />
            </div>
          </div>

          <div className={css({ textAlign: "left", width: "100%;" })}>
            <Chips
              text="左足"
              variant="backgroundDarkenContained"
              size="l"
              fontWeight="bold"
            />
          </div>

          <div className={hstack({ gap: 0, width: "100%" })}>
            <span
              className={css({ textStyle: "span.m.bold", minWidth: "99px" })}
            >
              幅(ワイズ)
            </span>
            <div className={valueContainer}>
              <span className={valueText}>
                {variantData?.left.foot_width.value}
              </span>
              <Chips
                tag="button"
                text="変更"
                variant="borderSecondaryOutlined"
                size="s"
                onClick={() => {
                  setPath("change_width_and_size");
                }}
                fontWeight="bold"
              />
            </div>
          </div>

          <div className={hstack({ gap: 0, width: "100%" })}>
            <span
              className={css({ textStyle: "span.m.bold", minWidth: "99px" })}
            >
              足長(サイズ)
            </span>
            <div className={valueContainer}>
              <span className={valueText}>
                {variantData?.left.foot_length.value}
              </span>
              <Chips
                tag="button"
                text="変更"
                variant="borderSecondaryOutlined"
                size="s"
                onClick={() => {
                  setPath("change_width_and_size");
                }}
                fontWeight="bold"
              />
            </div>
          </div>
        </div>

        <div className={css({ py: 3, mt: 3 })}>
          <div
            className={hstack({
              justify: "space-between",
              alignItems: "center",
              bgColor: "primary.contrast",
              boxShadow: "0px 0px 16px 0px #8080801A",
              py: 3,
              px: 2.5,
              mb: 4,
            })}
          >
            <span>金額：</span>
            <div
              className={hstack({
                gap: 0,
                alignItems: "baseline",
              })}
            >
              <span className={css({ textStyle: "span.xl" })}>
                &yen;{variantData?.totalPrice.toLocaleString()}
              </span>
              <span className={css({ textStyle: "span.m.regular" })}>
                （税込）
              </span>
            </div>
          </div>
        </div>
        {isAdditionalConsented(parentCode) ? (
          <div
            className={vstack({
              gap: 3,
              width: "100%",
            })}
          >
            <div
              className={hstack({
                gap: 0,
                alignContent: "center",
                justify: "left",
              })}
            >
              {isConsented.isConsented ? (
                <img
                  src={svgCheckboxChecked}
                  alt=""
                  onClick={() => {
                    onClickCheckbox({
                      isConsented: !isConsented.isConsented,
                      isConsented2: isConsented.isConsented2,
                    });
                  }}
                  className={css({ cursor: "pointer", w: "32px", h: "32px" })}
                />
              ) : (
                <img
                  src={svgCheckboxUnchecked}
                  alt=""
                  onClick={() => {
                    onClickCheckbox({
                      isConsented: !isConsented.isConsented,
                      isConsented2: isConsented.isConsented2,
                    });
                  }}
                  className={css({ cursor: "pointer", w: "32px", h: "32px" })}
                />
              )}
              <span
                className={css({
                  pl: 1.5,
                  cursor: "pointer",
                  textStyle: "paragraph.l",
                })}
                onClick={() => {
                  onClickCheckbox({
                    isConsented: !isConsented.isConsented,
                    isConsented2: isConsented.isConsented2,
                  });
                }}
              >
                左右異なるサイズを選択した場合、商品価格は8,990円税込となります。また、お届けは約3か月後になります。
              </span>
            </div>
            <div
              className={hstack({
                gap: 0,
                alignContent: "center",
                justify: "left",
              })}
            >
              {isConsented.isConsented2 ? (
                <img
                  src={svgCheckboxChecked}
                  alt=""
                  onClick={() => {
                    onClickCheckbox({
                      isConsented: isConsented.isConsented,
                      isConsented2: !isConsented.isConsented2,
                    });
                  }}
                  className={css({ cursor: "pointer", w: "32px", h: "32px" })}
                />
              ) : (
                <img
                  src={svgCheckboxUnchecked}
                  alt=""
                  onClick={() => {
                    onClickCheckbox({
                      isConsented: isConsented.isConsented,
                      isConsented2: !isConsented.isConsented2,
                    });
                  }}
                  className={css({ cursor: "pointer", w: "32px", h: "32px" })}
                />
              )}
              <span
                className={css({
                  pl: 1.5,
                  cursor: "pointer",
                  textStyle: "paragraph.l",
                })}
                onClick={() => {
                  onClickCheckbox({
                    isConsented: isConsented.isConsented,
                    isConsented2: !isConsented.isConsented2,
                  });
                }}
              >
                左右異なるサイズを選択した場合、ご注文キャンセル、不良品以外の返品・交換は承っておりません。予めご了承ください。
              </span>
            </div>
          </div>
        ) : (
          <div
            className={hstack({
              gap: 0,
              alignContent: "center",
              justify: "left",
            })}
          >
            {isConsented.isConsented ? (
              <img
                src={svgCheckboxChecked}
                alt=""
                onClick={() => {
                  onClickCheckbox({
                    isConsented: !isConsented.isConsented,
                    isConsented2: isConsented.isConsented2,
                  });
                }}
                className={css({ cursor: "pointer", w: "32px", h: "32px" })}
              />
            ) : (
              <img
                src={svgCheckboxUnchecked}
                alt=""
                onClick={() => {
                  onClickCheckbox({
                    isConsented: !isConsented.isConsented,
                    isConsented2: isConsented.isConsented2,
                  });
                }}
                className={css({ cursor: "pointer", w: "32px", h: "32px" })}
              />
            )}
            <span
              className={css({
                pl: 1.5,
                cursor: "pointer",
                textStyle: "paragraph.l",
              })}
              onClick={() => {
                onClickCheckbox({
                  isConsented: !isConsented.isConsented,
                  isConsented2: isConsented.isConsented2,
                });
              }}
            >
              注文キャンセル・不良品以外の返品は承っておりません。
            </span>
          </div>
        )}
        <div
          className={css({
            bg: "black.100",
            width: "100%",
            px: 3,
            position: "fixed",
            bottom: 0,
            left: 0,
            pt: 2,
            height: "134px",
            boxShadow: "0px 4px 15px 0px #00000040",
            zIndex: Infinity,
          })}
        >
          <div
            className={css({
              width: "100%",
              maxWidth: "760px",
              mx: "auto",
              textAlign: "center",
            })}
          >
            <SquareButton
              size="full"
              variant="contained"
              color="primary"
              onClick={onClickAddingToCart}
              disabled={
                isAdditionalConsented(parentCode)
                  ? !(isConsented.isConsented && isConsented.isConsented2)
                  : !isConsented.isConsented
              }
            >
              カート追加
            </SquareButton>
            <button
              className={css({ mt: 2, textStyle: "button.m" })}
              onClick={() => {
                setPath("select_width_and_size");
              }}
            >
              戻る
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
